import List from "@mui/material/List";
import { useEffect } from "react";
import theme from "../../assets/theme";
import SidebarMenuItem from "./SidebarMenuItem";
import FiberManualRecordOutlinedIcon from "@mui/icons-material/FiberManualRecordOutlined";
import OnDeviceTrainingIcon from "@mui/icons-material/OnDeviceTraining";
import CommitOutlinedIcon from "@mui/icons-material/CommitOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import Divider from "@mui/material/Divider";
import * as React from "react";
import { CSSObject, Theme } from "@mui/material";
import MuiDrawer from '@mui/material/Drawer';
import { DrawerHeader } from "./MainToolbar";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { styled } from "@mui/material/styles";
import {
    getBranchTableAbsPath,
    getCalculationTableAbsPath,
    getEquipmentTableAbsPath,
    getNodeTableAbsPath, getProjectSettingsTabAbsPath, getTeamTabAbsPath
} from "../../routes/paths";
import i18nStore from "../../i18n/store";


const drawerWidth = 240;


const Tabs = {
    Nodes: 1,
    Branches: 2,
    Equipments: 3,
    CalcResults: 4,
    ProjectTeam: 5,
    ProjectSettings: 6,
};

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        variants: [
            {
                props: ({ open }) => open,
                style: {
                    ...openedMixin(theme),
                    '& .MuiDrawer-paper': openedMixin(theme),
                },
            },
            {
                props: ({ open }) => !open,
                style: {
                    ...closedMixin(theme),
                    '& .MuiDrawer-paper': closedMixin(theme),
                },
            },
        ],
    }),
);


export interface ProjectMenuDrawerProps {
    open: boolean;
    ProjectMenuDrawerRef: React.MutableRefObject<any>;
}

const ProjectMenuDrawer = (props: ProjectMenuDrawerProps) => {
    const translations = i18nStore.getTranslations;
    const [openedProjectSubTab, setOpenedProjectSubTab] = React.useState<number>(1);
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const isInProject: boolean = Boolean(params.projectId);

    useEffect(() => {
        if (location.pathname.includes("nodes")) {
            setOpenedProjectSubTab(Tabs.Nodes);
        } else if (location.pathname.includes("equipments")) {
            setOpenedProjectSubTab(Tabs.Equipments);
        } else if (location.pathname.includes("branches")) {
            setOpenedProjectSubTab(Tabs.Branches);
        } else if (location.pathname.includes("calculations")) {
            setOpenedProjectSubTab(Tabs.CalcResults);
        } else if (location.pathname.includes("settings")) {
            setOpenedProjectSubTab(Tabs.ProjectSettings);
        }
    }, [location.pathname]);

    return (
        <>
            <Drawer variant="permanent" open={props.open} sx={{ display: isInProject ? "block" : "none" }}>
                <DrawerHeader />
                <List ref={props.ProjectMenuDrawerRef} sx={{
                    "& .Mui-selected": {
                        color: "white !important",
                        background: `${theme.palette.primary.main} !important`,
                    },
                }}>
                    <Divider/>
                    <SidebarMenuItem
                        text={translations.nodes}
                        open={props.open}
                        index={Tabs.Nodes}
                        openedIndex={openedProjectSubTab}
                        icon={<FiberManualRecordOutlinedIcon sx={{ color: Tabs.Nodes === openedProjectSubTab ? "white" : "inherit" }} />}
                        onClick={() => {
                            setOpenedProjectSubTab(Tabs.Nodes);
                            navigate(getNodeTableAbsPath(params.projectId));
                        }}
                    />
                    <SidebarMenuItem
                        text={translations.branches}
                        open={props.open}
                        index={Tabs.Branches}
                        openedIndex={openedProjectSubTab}
                        icon={<CommitOutlinedIcon sx={{ color: Tabs.Branches === openedProjectSubTab ? "white" : "inherit" }} />}
                        onClick={() => {
                            setOpenedProjectSubTab(Tabs.Branches);
                            navigate(getBranchTableAbsPath(params.projectId));
                        }}
                    />
                    <SidebarMenuItem
                        text={translations.equipments}
                        open={props.open}
                        index={Tabs.Equipments}
                        openedIndex={openedProjectSubTab}
                        icon={<OnDeviceTrainingIcon sx={{ color: Tabs.Equipments === openedProjectSubTab ? "white" : "inherit" }} />}
                        onClick={() => {
                            setOpenedProjectSubTab(Tabs.Equipments);
                            navigate(getEquipmentTableAbsPath(params.projectId));
                        }}
                    />
                    <SidebarMenuItem
                        text={translations.calculations}
                        open={props.open}
                        index={Tabs.CalcResults}
                        openedIndex={openedProjectSubTab}
                        icon={<BoltOutlinedIcon sx={{ color: Tabs.CalcResults === openedProjectSubTab ? "white" : "inherit" }} />}
                        onClick={() => {
                            setOpenedProjectSubTab(Tabs.CalcResults);
                            navigate(getCalculationTableAbsPath(params.projectId));
                        }}
                    />
                    <Divider/>
                    <SidebarMenuItem
                        text={translations.settings}
                        open={props.open}
                        index={Tabs.ProjectSettings}
                        openedIndex={openedProjectSubTab}
                        icon={<SettingsOutlinedIcon sx={{ color: Tabs.ProjectSettings === openedProjectSubTab ? "white" : "inherit" }} />}
                        onClick={() => {
                            setOpenedProjectSubTab(Tabs.ProjectSettings);
                            navigate(getProjectSettingsTabAbsPath(params.projectId));
                        }}
                    />
                    <Divider/>
                </List>
            </Drawer>
        </>
    );
};

export default ProjectMenuDrawer;
